import { datadogLogs } from '@datadog/browser-logs'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { PanelSupplierCode } from 'model/common'
import { AddSliderResponsePayload } from 'model/newApiPayloads'
import { ResponseOptionChoice } from 'model/responseChoice'
import { useContext } from 'react'
import { coreRespondentUrl } from 'utils/coreRespondentUrl'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import { getUtcOffset } from 'utils/datetime'
import { usePostCallbackWithRetry } from './usePostCallback'
import { useQuestionnaireParams } from './useQuestionnaireParams'

const useSendSliderResponse = () => {
  const post = usePostCallbackWithRetry<AddSliderResponsePayload>()
  const { surveyId, respondentId, panelSupplier } = useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress]
  } = useContext<AppState>(AppStateContext)
  const { next } = useContext(PfmContext)

  const sendSliderResponse = async ({
    questionId,
    entryPosition,
    renderedEntryPosition,
    contextPosition,
    renderedContextPosition,
    responseChoices
  }: {
    questionId: string
    entryPosition: number
    renderedEntryPosition: number
    contextPosition: number
    renderedContextPosition: number
    responseChoices: ResponseOptionChoice[]
  }) => {
    const choice = responseChoices[0]
    const payload: AddSliderResponsePayload = {
      supplier_respondent_id: respondentId,
      panel_supplier_code: panelSupplier as PanelSupplierCode,
      question_id: questionId,
      rendered_entry_position: renderedEntryPosition,
      response_choice: {
        value: choice.value as string,
        response_datetime: choice.responseDatetime,
        response_option_id: choice.responseOptionId ?? null,
        position: choice.position ?? null,
        rendered_position: choice.renderedPosition ?? null
      },
      entry_position: entryPosition,
      previous_entry_position: progress?.previousEntryPosition ?? null,
      context_position: contextPosition,
      rendered_context_position: renderedContextPosition,
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      utc_offset: getUtcOffset()
    }
    const url = coreRespondentUrl(`/surveys/${surveyId}/responses/slider`)

    const requestStartTime = performance.now()

    const promise = post(payload, url)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Slider response was sent', {
          questionId,
          questionTypeCode: 'scale',
          surveyId,
          respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW,
          requestDurationMs: Math.round(requestDuration),
          payloadSize: JSON.stringify(payload).length
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.error(
          'Network Failure: Slider response failed to be sent',
          {
            err,
            httpStatusCode: err?.response?.status ?? null,
            questionId,
            questionTypeCode: 'scale',
            surveyId,
            respondentId,
            apiUsed: API_DISTRIBUTION_FACET.NEW,
            payloadSize: JSON.stringify(payload).length,
            requestDurationMs: Math.round(requestDuration)
          }
        )
        if (progress) {
          setProgress({
            ...progress,
            isQualityTerminated: true
          })
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendSliderResponse'
          )
        }
      })

    return promise
  }

  return { sendSliderResponse }
}

export default useSendSliderResponse
